import { useState, useCallback, useMemo } from 'react';

import { Button } from '@material-ui/core';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

import { Select } from 'components';
import { aboutContext } from 'contexts';
import { EntityType } from 'enums';
import {
  UserBrandEntity,
  UserSchoolEntity,
  useUpdateIndicatorsByContextLazyQuery,
} from 'generated';
import { useBootstrapApp } from 'hooks';
import { formatIndicatorName, safeStorage } from 'utils';

import { EntitySchool } from './components';
import { useStyles } from './styles';

const searchStorageKey = 'schools:search';

export function EntitySelection(): JSX.Element {
  const {
    entityType,
    updateEntity,
    updateSimiliarSchoolsCompare,
    schools,
    brands,
    entity,
    globalLoading,
    updateGlobalLoading,
    indicatorEventId,
    updateContexts,
    updateCurrentContext,
    updateCurrentIndicator,
    hasCompanyPermission,
  } = useBootstrapApp();
  const { rootStyle, entityBtn, entityBtnActive } = useStyles();
  const history = useHistory();

  const [schoolsModalIsOpen, setSchoolsModalIsOpen] = useState<boolean>(false);

  const [updateIndicators] = useUpdateIndicatorsByContextLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted(newData) {
      const { data } = newData?.indicatorsByContext || {};
      const path = `/about?entityId=${entity?.id}&entityType=${entityType}&indicatorEventId=${indicatorEventId}`;

      if (data) {
        const context = aboutContext;
        const [indicator] = aboutContext.indicators;

        updateContexts(formatIndicatorName(data));
        updateCurrentContext(context);
        updateCurrentIndicator(indicator);
        history.replace(path);
      }
      updateGlobalLoading(false);
    },
  });

  const handleUpdateContext = useCallback(
    (newEntityType: EntityType) => {
      updateGlobalLoading(true);
      updateIndicators({
        variables: {
          context: newEntityType,
          indicatorEventId,
        },
      });
    },
    [indicatorEventId, updateIndicators, updateGlobalLoading],
  );

  const handleItemChange = useCallback(
    (entity: UserBrandEntity | UserSchoolEntity) => {
      updateEntity(entity, EntityType.SCHOOL);
      setSchoolsModalIsOpen(false);
      handleUpdateContext(EntityType.SCHOOL);
    },
    [updateEntity, handleUpdateContext],
  );

  const handleChangeEntity = useCallback(
    (
      selectedEntity: UserBrandEntity | UserSchoolEntity,
      newEntityType: EntityType,
    ) => {
      if (newEntityType !== EntityType.SCHOOL) {
        updateSimiliarSchoolsCompare(false);
      }
      updateEntity(selectedEntity, newEntityType);
      handleUpdateContext(newEntityType);
      safeStorage.removeItem(searchStorageKey);
    },
    [handleUpdateContext, updateEntity, updateSimiliarSchoolsCompare],
  );

  const memoSelectedBrand = useMemo((): any => {
    if (entityType !== EntityType.BRAND) {
      return '';
    }

    return entity;
  }, [entity, entityType]);

  return (
    <>
      <EntitySchool
        searchStorageKey={searchStorageKey}
        openInitial={schoolsModalIsOpen}
        onChangeEntity={handleItemChange}
        fnClose={() => setSchoolsModalIsOpen(false)}
      />
      <div className={rootStyle}>
        {!!hasCompanyPermission && (
          <Button
            disabled={globalLoading}
            className={clsx(entityBtn, {
              [entityBtnActive]: entityType === EntityType.COMPANY,
            })}
            color="primary"
            variant="outlined"
            onClick={() =>
              handleChangeEntity(
                { id: 'SOMOS', name: 'SOMOS', brandNames: [] },
                EntityType.COMPANY,
              )
            }
          >
            Rede
          </Button>
        )}
        {!!brands?.data?.length && (
          <Select<UserBrandEntity | UserSchoolEntity>
            data={brands.data}
            active={entityType === EntityType.BRAND}
            label="Ir para uma marca"
            onChangeValue={newBrand =>
              handleChangeEntity(newBrand, EntityType.BRAND)
            }
            value={memoSelectedBrand}
          />
        )}
        {!!schools?.data.length && (
          <Button
            disabled={globalLoading}
            className={clsx({
              [entityBtnActive]: entityType === EntityType.SCHOOL,
              [entityBtn]: entityType !== EntityType.SCHOOL,
            })}
            color="primary"
            variant="outlined"
            onClick={() => setSchoolsModalIsOpen(true)}
          >
            Ir para {entityType === EntityType.SCHOOL ? 'outra' : 'uma'} escola
          </Button>
        )}
      </div>
    </>
  );
}
